import type {
  Header_Ausflüge,
  Header_Services,
} from "@aleksundshantu/gp-types/acf-header";
import type {
  Modul_Ausflüge_Grid,
  Modul_Services_Grid,
} from "@aleksundshantu/gp-types/acf-module";
import type { TextCardElement } from "@/components/TextCard/TextCard";

/** The ACF layouts rendered by the shared CardGrid module. */
export type CardGridModuleType = Modul_Services_Grid | Modul_Ausflüge_Grid;

// Derived from the ACF types so a backend field change breaks the build.
export type CardGridSettings = Modul_Services_Grid["highlights_grid"];
export type CardGridLayout = Modul_Services_Grid["layout"];

/** Every ACF header payload a card grid may read. */
export type CardGridAcf = Header_Services | Header_Ausflüge;

export type CardGridSourceKey = "services" | "ausfluege";
export type CardGridTaxonomy = "services_category" | "ausfluege_category";

export interface CardGridSource {
  /** WP REST post type endpoint */
  postType: CardGridSourceKey;
  /** WP REST taxonomy query parameter */
  taxonomy: CardGridTaxonomy;
  /** suffix for the anime refresh key */
  animeKeyExt: string;
  /** placeholder rendered while the skeleton is visible */
  skeletonCard: TextCardElement;
  /** adapter: one post's ACF payload -> the shared card element */
  mapPostToCard: (acf: CardGridAcf | undefined) => TextCardElement;
}

const mapServicePostToCard = (
  acf: CardGridAcf | undefined,
): TextCardElement => {
  const header =
    acf && "header_services" in acf ? acf.header_services : undefined;

  return {
    title: header?.title || "",
    subline: header?.subline || "",
    price: header?.price || "",
    // the ACF field name carries the typo, the card type does not
    description: header?.descriptuon || "",
    location: header?.location || "",
  };
};

const mapAusfluegePostToCard = (
  acf: CardGridAcf | undefined,
): TextCardElement => {
  const header =
    acf && "header_ausfluege" in acf ? acf.header_ausfluege : undefined;

  return {
    title: header?.title || "",
    subline: header?.subline || "",
    // Ausflüge have no price field, so the info line is not rendered
    price: "",
    description: header?.description || "",
    location: header?.location || "",
  };
};

export const cardGridSources: Record<CardGridSourceKey, CardGridSource> = {
  services: {
    postType: "services",
    taxonomy: "services_category",
    animeKeyExt: "services-grid-category-filter",
    skeletonCard: {
      title: "Service placeholder",
      subline: "Service placeholder subline",
      price: "ab 49,- EUR",
      description:
        "Service placeholder description for the loading skeleton state in this card.",
      location: "Garten Spa",
    },
    mapPostToCard: mapServicePostToCard,
  },
  ausfluege: {
    postType: "ausfluege",
    taxonomy: "ausfluege_category",
    animeKeyExt: "ausfluege-grid-category-filter",
    skeletonCard: {
      title: "Ausflug placeholder",
      subline: "",
      price: "",
      description:
        "Ausflug placeholder description for the loading skeleton state in this card.",
      location: "",
    },
    mapPostToCard: mapAusfluegePostToCard,
  },
};

/** Single normalization point for the two ACF field group keys. */
export const resolveCardGrid = (
  data: CardGridModuleType,
): { sourceKey: CardGridSourceKey; settings: CardGridSettings } =>
  "ausfluege_grid" in data
    ? { sourceKey: "ausfluege", settings: data.ausfluege_grid }
    : { sourceKey: "services", settings: data.highlights_grid };
